<template>
    <Draggable
        tag="ol"
        v-model="segments"
        :disabled="disabled || !sortable"
        handle=".draggable-handle"
        :forceFallback="true"
        axis="y"
        @change="handleChange"
    >
        <li v-for="segment in segments" :key="segment.segmentId">
            <SegmentListItem
                :name="segment.segmentName"
                :exceptions="segment.exceptions"
                :conditions="segment.conditions"
                :metaData="segment.metaData"
                :placementNames="placementNames"
                :loading="segment.segmentId === loadingSegmentId"
                :disabled="disabled"
                :id="segment.segmentId"
                :testGroup="testGroup"
                :appKey="appKey"
                @remove="$emit('removeItem', segment.segmentId)"
            />
            <IconBase
                v-if="sortable"
                class="draggable-handle"
                iconName="dragHandleBigIcon"
                :iconColor="consts.COLORS.LIGHT_700"
                :iconHoverColor="consts.COLORS.PRIMARY_500"
                display="flex"
                width="9"
                height="24"
            />
        </li>
    </Draggable>
</template>

<script>
import Draggable from 'components/draggable/Draggable.vue';
import IconBase from 'components/IconBase.vue';
import { sortBy } from 'lodash';
import consts from 'services/metaData/consts';
import SegmentListItem from './SegmentListItem.vue';

export default {
    name: 'SegmentList',
    components: {
        SegmentListItem,
        IconBase,
        Draggable
    },
    props: {
        segmentsData: {
            type: Array,
            required: true
        },
        placementsData: {
            type: Object,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        testGroup: String,
        appKey: String
    },
    computed: {
        sortable() {
            return this.segments.length > 1;
        },
        placementNames() {
            // this makes placement names map object from placements data:
            // {"242_0":"DefaultRewardedVideo", "242_2":"PlacementName", ... }
            const result = {};
            Object.entries(this.placementsData).forEach(([adUnitId, placements]) => {
                placements.forEach((placement) => {
                    result[`${adUnitId}_${placement.id}`] = placement.name;
                });
            });
            return result;
        },
        consts: () => consts
    },
    data() {
        return {
            segments: [],
            loadingSegmentId: null
        };
    },
    methods: {
        refreshInternalList() {
            // clear loader
            this.loadingSegmentId = null;
            // update internal segments list
            this.segments = sortBy(this.segmentsData, 'segmentPosition');
        },
        handleChange({ newIndex, oldIndex, itemData }) {
            const { segmentId } = itemData;
            // show loader for moved segment
            this.loadingSegmentId = segmentId;
            // emit event with new order
            this.$emit('reorder', {
                segmentsPositions: this.segments.map((s) => s.segmentId),
                newIndex,
                oldIndex
            });
        }
    },
    mounted() {
        this.refreshInternalList();
    },
    watch: {
        segmentsData() {
            this.refreshInternalList();
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";

ol {
    list-style: none;
    counter-reset: segment-number;
    margin: 4px 0;
    padding-left: 25px;
    > li {
        position: relative;
        counter-increment: segment-number;
        // list item number
        &:before {
            content: counter(segment-number);
            display: block;
            position: absolute;
            left: -21px;
            top: 14px;
            @include font(3);
            color: $light-800;
        }
    }
}

// Sortable Placeholder
.sortable-ghost {
    &:after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: $primary-200;
        border-radius: $borderRadius;
        border: 1px dashed $light-600;
    }
    > div {
        box-shadow: none;
    }
}

// Draggable Item
.sortable-drag {
    cursor: grab;
    background: $light-100;
    // list item number
    &:before {
        display: none;
    }

    .segment-list-item {
        margin: 0;
    }
}

// Draggable Handle
.draggable-handle {
    position: absolute;
    right: 6px;
    top: 11px;
    width: 29px;
    padding: 2px 10px;
    cursor: move;
}
</style>
